import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  UserPlus,
  Briefcase,
  ArrowRightLeft,
  MessageSquare,
  Mail,
  CalendarCheck,
  AlertCircle,
  CheckCheck,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '@/lib/api';
import type { Notification } from '@/types';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

function getNotificationIcon(type: string, category?: string) {
  switch (category || type) {
    case 'candidate_created':
    case 'candidate_updated':
      return <UserPlus className="h-4 w-4 text-emerald-400" />;
    case 'job_created':
    case 'job_updated':
      return <Briefcase className="h-4 w-4 text-blue-400" />;
    case 'stage_change':
    case 'application_created':
      return <ArrowRightLeft className="h-4 w-4 text-amber-400" />;
    case 'note_added':
    case 'mention':
      return <MessageSquare className="h-4 w-4 text-purple-400" />;
    case 'email_sent':
    case 'email_received':
      return <Mail className="h-4 w-4 text-cyan-400" />;
    case 'interview_scheduled':
    case 'interview':
      return <CalendarCheck className="h-4 w-4 text-orange-400" />;
    case 'invoice':
      return <Mail className="h-4 w-4 text-cyan-400" />;
    case 'submission':
      return <MessageSquare className="h-4 w-4 text-purple-400" />;
    case 'placement':
      return <Briefcase className="h-4 w-4 text-blue-400" />;
    case 'alert':
      return <AlertCircle className="h-4 w-4 text-red-400" />;
    default:
      return <Bell className="h-4 w-4 text-text-tertiary" />;
  }
}

function getEntityPath(entityType: string | null, entityId: string | null): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'candidate':
      return `/candidates/${entityId}`;
    case 'job':
      return `/jobs/${entityId}`;
    case 'application':
      return `/pipeline`;
    case 'company':
      return `/companies/${entityId}`;
    case 'invoice':
      return `/invoices/${entityId}`;
    case 'placement':
      return `/placements/${entityId}`;
    case 'interview':
      return '/interviews';
    case 'submission':
      return '/submissions';
    default:
      return null;
  }
}

function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'page', page, unreadOnly],
    queryFn: () => fetchNotifications({ page, limit: pageSize, unreadOnly }),
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  function handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    const path = getEntityPath(notification.entityType, notification.entityId);
    if (path) {
      navigate(path);
    }
  }

  const notifications = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Notifications</h1>
          <p className="text-text-secondary mt-1">
            Stay updated on your recruitment activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Unread Only Toggle */}
          <button
            onClick={() => {
              setUnreadOnly(!unreadOnly);
              setPage(1);
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors border ${
              unreadOnly
                ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-400'
                : 'bg-transparent border-white/10 text-text-secondary hover:bg-white/5'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${unreadOnly ? 'bg-emerald-500' : 'bg-text-tertiary'}`} />
            Unread only
          </button>

          {/* Mark All Read */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCheck className="h-4 w-4 mr-2" />
            )}
            Mark all as read
          </Button>
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-white/5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-4 flex gap-4">
                <Skeleton className="h-10 w-10 rounded-full flex-shrink-0 bg-white/5" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3 bg-white/5" />
                  <Skeleton className="h-3 w-full bg-white/5" />
                  <Skeleton className="h-3 w-24 bg-white/5" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="h-12 w-12 text-text-tertiary mx-auto mb-3" />
            <h3 className="text-lg font-medium text-text-primary mb-1">
              {unreadOnly ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-sm text-text-tertiary">
              {unreadOnly
                ? 'You\'re all caught up! Toggle off the filter to see all notifications.'
                : 'Notifications about your recruitment activity will appear here.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {notifications.map((notification) => {
              const entityPath = getEntityPath(notification.entityType, notification.entityId);
              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left p-4 flex gap-4 hover:bg-white/5 transition-colors ${
                    !notification.isRead ? 'bg-white/[0.02]' : ''
                  } ${entityPath ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-white/5 flex items-center justify-center">
                    {getNotificationIcon(notification.type, notification.category)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className={`text-sm ${!notification.isRead ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-text-tertiary mt-0.5">
                          {notification.message}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-text-tertiary whitespace-nowrap">
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                        {!notification.isRead && (
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-tertiary">
            Page {page} of {totalPages}
            {pagination?.total !== undefined && (
              <> &middot; {pagination.total} total notifications</>
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
